import React, { useState } from "react";
import axios from "axios";
import "../styles/Login.css";
import ills from "../image/illustrate.png";
import logo from "../image/logoname.png";
import illsMobile from "../image/illustration.png";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import { useLocation, useNavigate } from "react-router-dom";
import Loader from "./Loader";
import { signInWithPopup } from "firebase/auth";
import { auth, googleProvider } from "./FirebaseConfig";
import { setTokens, clearTokens } from "../utils/authService";

const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    uid: "",
    password: "",
  });
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);
    
    try {
      const response = await axios.post(
        "https://api.nexcard.co.in/api/auth/jwt/create/", 
        formData
      );

      // Clear old tokens and storage
      clearTokens();

      // Store tokens in Secure cookies (mobile app-like behavior)
      setTokens(response.data.access, response.data.refresh);

      // Check if password needs to be reset
      if (response.data.is_password_reset === "False") {
        navigate("/reset-password", {
          state: {
            accessToken: response.data.access,
            refreshToken: response.data.refresh
          }
        });
        return;
      }

      // FIX: Check where the user was trying to go originally
      const redirectTo = location.state?.from;
      
      if (redirectTo) {
        // If there's a specific destination, go there
        navigate(redirectTo);
      } else {
        // Otherwise go to default page
        navigate("/home-web-holder", {
          state: {
            ...location.state,
            isHolderShare: location.state?.isHolderShare
          }
        });
      }

    } catch (error) {
      console.error("Login error:", error);
      
      if (error.response) {
        const backendError = error.response.data;
        
        if (backendError.detail) {
          setError(backendError.detail);
        } else if (backendError.non_field_errors) {
          setError(backendError.non_field_errors.join(' '));
        } else if (backendError.uid || backendError.password) {
          const fieldErrors = [];
          if (backendError.uid) fieldErrors.push(`Email: ${backendError.uid.join(' ')}`);
          if (backendError.password) fieldErrors.push(`Password: ${backendError.password.join(' ')}`);
          setError(fieldErrors.join('. '));
        } else {
          setError("Login failed. Please try again.");
        }
      } else if (error.request) {
        setError("Network error. Please check your internet connection.");
      } else {
        setError("An unexpected error occurred. Please try again.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const result = await signInWithPopup(auth, googleProvider);
      const user = result.user;
      const idToken = await user.getIdToken();

      const response = await axios.get(
        "https://api.nexcard.co.in/api/auth/users/me/", 
        {
          headers: { Authorization: `Firebase ${idToken}` }
        }
      );

      // Clear old tokens and storage
      clearTokens();

      // Store Firebase token in sessionStorage (for Firebase auth)
      sessionStorage.setItem("firebaseToken", idToken);
      
      // Store access and refresh tokens in Secure cookies (mobile app-like behavior)
      setTokens(response.data.tokens.access, response.data.tokens.refresh);

      // FIX: Check where the user was trying to go originally
      const redirectTo = location.state?.from;
      
      if (redirectTo) {
        // If there's a specific destination, go there
        navigate(redirectTo);
      } else {
        // Otherwise go to default page
        navigate("/home-web-holder", {
          state: {
            requireCard: location.state?.requireCard || false
          }
        });
      }
    } catch (error) {
      console.error("Google Sign-In failed:", error);
      
      if (error.response) {
        const backendError = error.response.data;
        if (backendError.detail) {
          setError(backendError.detail);
        } else if (backendError.message) {
          setError(backendError.message);
        } else {
          setError("Google authentication failed. Please try again.");
        }
      } else if (error.code) {
        switch (error.code) {
          case 'auth/account-exists-with-different-credential':
            setError("An account already exists with this email. Try logging in with email/password.");
            break;
          case 'auth/popup-closed-by-user':
            setError("Sign-in window was closed. Please try again.");
            break;
          case 'auth/network-request-failed':
            setError("Network error. Please check your internet connection.");
            break;
          default:
            setError("Google sign-in failed: " + error.message);
        }
      } else {
        setError("An unexpected error occurred during Google sign-in.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };
  
  const handleForgotPassword = async () => {
    if (!formData.uid) {
      setError("Please enter your email address");
      return;
    }
  
    setIsLoading(true);
    setError(null);
  
    try {
      const response = await axios.post(
        "https://api.nexcard.co.in/api/password/forgot/",
        { email: formData.uid }
      );
  
      // Show success message from API response
      setError("Please check your email for a temporary password");
      console.log(response);
      
    } catch (error) {
      console.error("Forgot password error:", error);
      
      if (error.response) {
        const backendError = error.response.data;
        if (backendError.detail) {
          setError(backendError.detail);
        } else if (backendError.email) {
          setError(backendError.email.join(' '));
        } else {
          setError("Failed to send password reset email. Please try again.");
        }
      } else if (error.request) {
        setError("Network error. Please check your internet connection.");
      } else {
        setError("An unexpected error occurred. Please try again.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Get current year for copyright
  const currentYear = new Date().getFullYear();

  return (
    <div className="login-container">
      <div className="login-left">
        {/* Mobile Logo Section - Added */}
        <div className="mobile-logo-container">
          <img src={logo} alt="NexCard Logo" className="mobile-logo" />
        </div>
        
        {/* Desktop Logo */}
        <div className="logoN">
          <img src={logo} alt="NexCard Logo" />
        </div>
        
        <div className="mobile-illustration">
          <img src={illsMobile} alt="Mobile Illustration" />
        </div>
        <h1 className="welcome">Welcome back</h1>
        <p className="welcomeP">Welcome back! Sign in to continue with <b>NexCard</b></p>
        <form className="login-form" onSubmit={handleSubmit}>
          <label>Email</label>
          <input
            type="email"
            name="uid"
            placeholder="Enter your email"
            value={formData.uid}
            onChange={handleChange}
            required
          />
          <div className="password-container4">
            <label>Password</label>
            <div className="input-wrapper4">
              <input
                type={showPassword ? "text" : "password"}
                name="password"
                placeholder="******"
                value={formData.password}
                onChange={handleChange}
                required
                className="password-input4"
              />
              <span className="eye-icon4" onClick={togglePasswordVisibility}>
                {showPassword ? <FaEyeSlash /> : <FaEye />}
              </span>
            </div>
          </div>
          {error && <div className="error-message">{error}</div>}
          <div className="options">
            {/* Commented out the Remember me checkbox */}
            {/* <div className="remember-container">
              <input type="checkbox" id="remember" />
              <label htmlFor="remember">Remember for 30 Days</label>
            </div> */}
            <button 
              type="button" 
              className="forgot-password-btn"
              onClick={handleForgotPassword}
              disabled={isLoading}
            >
              Forgot password
            </button>
          </div>
          <button type="submit" className="sign-in-btn" disabled={isLoading}>
            {isLoading ? <Loader /> : "Sign in"}
          </button>
          <button
            type="button"
            className="google-sign-in"
            onClick={handleGoogleSignIn}
            disabled={isLoading}
          >
            <img src="https://upload.wikimedia.org/wikipedia/commons/3/3c/Google_Favicon_2025.svg" alt="Google Icon" /> Sign in with Google
          </button>
        </form>
        <p className="signup">
          Don't have an account?{" "}
          <button type="button" className="sign-up-btn" onClick={() => navigate("/signup")}>
            Sign up for free
          </button>
        </p>
        
        {/* Mobile Footer - Only visible on mobile */}
        <div className="mobile-footer">
          <div className="footer-content">
            <span className="footer-brand">NexCard</span>
            <span className="footer-separator">|</span>
            <span className="footer-support">support@nexcard.co.in</span>
            <span className="footer-separator">|</span>
            <span className="footer-copyright">© {currentYear} All rights reserved</span>
          </div>
        </div>
      </div>
      <div className="login-divider"></div>
      <div className="login-right">
        <img src={ills} alt="Background Illustration" className="background-image" />
      </div>
      
      {/* Desktop Footer - Centered at the bottom of the whole container */}
      <div className="desktop-footer">
        <div className="footer-content">
          <span className="footer-brand">NexCard</span>
          <span className="footer-separator">|</span>
          <span className="footer-support">enquiry@nexcard.co.in</span>
          <span className="footer-separator">|</span>
          <span className="footer-copyright">© {currentYear} All rights reserved</span>
        </div>
      </div>
    </div>
  );
};

export default Login;